import { Menu } from "./menu/menu";
import { BoxConfig, ExportedStage } from "../stage";
import { ServiceLocator } from "../service-locator";
import * as ClipboardJS from "clipboard";
import { ColorType, KonvaBox } from "../box-service";
import { useEffect, useRef } from "react";
import { Controller } from "../controller";
import { toast, ToastContainer, Zoom } from "react-toastify";
import { ControlButtons } from "./control-buttons";
import Konva from "konva";
import { Cart } from "./cart";

export const App = () => {
  const currentBox = useRef(undefined);

  const stage = new ExportedStage();

  const images = ServiceLocator.images;
  const navigation = ServiceLocator.navigation;
  const boxService = ServiceLocator.boxService;
  const uiService = ServiceLocator.uiService;

  const loadConfig = () => {
    new ClipboardJS("#share-config-button");

    const queryString = getQuery();

    const debugEnabled = !!queryString["debug"];

    stage.changeIsDebug(debugEnabled);

    images.addImagesLoadedListener(() => {
      const loadingElement = document.getElementById("loader");

      if (loadingElement) {
        loadingElement.style.display = "none";
      }

      const addCubeElements = document.querySelectorAll(".add-cube");

      addCubeElements.forEach((c) => {
        if (
          c.classList.contains("opacity-50") &&
          c.classList.contains("pointer-events-none")
        ) {
          c.classList.remove("opacity-50");
          c.classList.remove("pointer-events-none");
        }
      });
    });
  };

  const loadShareLink = () => {
    const queryString = getQuery();

    const boxesEncoded = queryString["boxes"];
    const zoomQs = queryString["zoom"];
    const viewQs = queryString["view"];
    const stagePositionXQs = queryString["sx"];
    const stagePositionYQs = queryString["sy"];

    if (queryString["box_color"]) {
      stage.setColor(queryString["box_color"] as ColorType);
    }

    if (viewQs) {
      stage.setView(viewQs);
    }

    if (boxesEncoded) {
      const boxes = navigation.decodeBoxes(boxesEncoded);

      images.addImagesLoadedListener(() => {
        for (const box of boxes) {
          addShelf(box);
        }

        stage.refocusBoxes();
      });
    }

    if (stagePositionXQs && stagePositionYQs) {
      stage.setStagePosition(+stagePositionXQs, +stagePositionYQs);
    }

    if (zoomQs) {
      stage.setZoom(+zoomQs);
    }

    setView();

    Controller.updateCubesMenu();
  };

  const loadStage = () => {
    const PADDING = 0;
    const konvaStage = new Konva.Stage({
      container: "container",
      width: document.getElementById("main").offsetWidth + PADDING * 2,
      height: document.getElementById("main").offsetHeight + PADDING * 2,
      draggable: true,
    });
    const konvaLayer = new Konva.Layer();

    konvaStage.add(konvaLayer);
    konvaLayer.draw();

    konvaStage.scale({ x: 1.2 / 5, y: 1.2 / 5 });
    konvaStage.on("dragmove", () => {
      let x = konvaStage.x();
      let y = konvaStage.y();

      if (x > -10 || y > -10) {
        if (x > -10) {
          x = -10;
        }
        if (y > -10) {
          y = -10;
        }

        konvaStage.position({ x: x, y: y });

        konvaStage.draw();
      }
    });

    stage.setStage(konvaStage);
    stage.setLayer(konvaLayer);
  };

  useEffect(() => {
    loadStage();
    loadConfig();
    loadShareLink();
  }, []);

  const setView = () => {
    const toggleButton = document.getElementById("toggle-view-button");

    if (toggleButton) {
      if (stage.isIsoView()) {
        toggleButton.innerHTML = "3D";
      } else {
        toggleButton.innerHTML = "Front";
      }
    }
  };

  const addShelf = (config: BoxConfig) => {
    if (!images.areImagesLoaded()) {
      console.log("Images not loaded yet");

      return;
    }

    const box = stage.addShelf(config);

    if (box) {
      box.on("dragmove", function () {
        document.body.style.cursor = "grabbing";
      });
      box.on("dragend", function () {
        document.body.style.cursor = "pointer";

        if (currentBox.current) {
          currentBox.current.strokeWidth(8);
        }

        updateShareLink();
      });
      box.on("click tap", function () {
        selectShelf(box);
      });
      box.on("mouseover", function () {
        document.body.style.cursor = "pointer";
      });
      box.on("mouseout", function () {
        document.body.style.cursor = "default";
      });

      updateShareLink();

      updateCheckout();
    }
  };

  const updateShareLink = () => {
    const boxesQs = navigation.encodeBoxes(boxService.getBoxes());
    const zoomQs = stage.getZoom().toFixed(2);
    const viewQs = stage.getView();
    const stagePosition = stage.getStagePosition();

    const currentQueryString = getQuery(window.location.search);

    currentQueryString["boxes"] = boxesQs;
    currentQueryString["zoom"] = zoomQs;
    currentQueryString["view"] = viewQs;
    currentQueryString["box_color"] = stage.getColor();
    currentQueryString["sx"] = stagePosition.x.toFixed(2);
    currentQueryString["sy"] = stagePosition.y.toFixed(2);

    const newQueryString =
      "?" +
      Object.entries(currentQueryString)
        .filter((qs) => !!qs[0] && qs[1] !== undefined)
        .map((qs) => qs[0] + "=" + qs[1])
        .join("&");

    const shareElement = document.getElementById("share-config-button");
    if (shareElement) {
      shareElement.setAttribute(
        "data-clipboard-text",
        window.location.origin + window.location.pathname + newQueryString,
      );
    }
  };

  const updateCheckout = () => {
    const total = boxService.getCartCost();

    const checkoutButtonText = document.querySelector("#checkout-button-text");

    if (checkoutButtonText) {
      checkoutButtonText.innerHTML = "View Cart ($ " + total.toFixed(0) + ")";
    }
  };

  const getQuery = (
    url: string = window.location.search,
  ): Record<string, string> => {
    const queryParams: Record<string, string> = {};

    const queryString = url.substring(url.indexOf("?") + 1);
    const pairs = queryString.split("&");

    for (const pair of pairs) {
      const [key, value] = pair.split("=");
      if (key) {
        queryParams[key] = decodeURIComponent(value || "");
      }
    }

    return queryParams;
  };

  const selectShelf = (box: KonvaBox) => {
    if (currentBox.current) {
      currentBox.current.strokeWidth(0);
      currentBox.current = undefined;
    } else {
      currentBox.current = box;
      currentBox.current.stroke("blue");
      currentBox.current.strokeWidth(8);
    }

    updateCubeMenu();
  };

  const updateCubeMenu = () => {
    Controller.updateCubeMenu(currentBox.current);
  };

  const changeAccessory = (action: () => void) => {
    if (!currentBox.current) {
      return;
    }

    action();

    drawCurrentBox();

    updateCubeMenu();

    updateShareLink();

    updateCheckout();
  };

  const drawCurrentBox = () => {
    if (currentBox.current) {
      stage.drawBox(currentBox.current);
    }
  };

  const toggleBackVisibility = () => {
    changeAccessory(() =>
      boxService.toggleBack(currentBox.current as KonvaBox),
    );
  };
  const toggleDoorVisibility = () => {
    changeAccessory(() =>
      boxService.toggleDoor(currentBox.current as KonvaBox),
    );
  };
  const toggleShelfVisibility = () => {
    changeAccessory(() =>
      boxService.toggleShelf(currentBox.current as KonvaBox),
    );
  };
  const toggleTopDrawerVisibility = () => {
    changeAccessory(() =>
      boxService.toggleDrawerTop(currentBox.current as KonvaBox),
    );
  };
  const toggleMiddleDrawerVisibility = () => {
    changeAccessory(() =>
      boxService.toggleDrawerMiddle(currentBox.current as KonvaBox),
    );
  };
  const toggleBottomDrawerVisibility = () => {
    changeAccessory(() =>
      boxService.toggleDrawerBottom(currentBox.current as KonvaBox),
    );
  };
  const toggleCornerDirection = () => {
    changeAccessory(() =>
      boxService.toggleDirection(currentBox.current as KonvaBox),
    );
  };
  const toggleCrossDivider = () => {
    changeAccessory(() =>
      boxService.toggleCrossDivider(currentBox.current as KonvaBox),
    );
  };

  const deleteShelf = () => {
    if (currentBox.current) {
      currentBox.current.destroy();

      const konvaStage = stage.getStage();

      konvaStage.draw();

      boxService.deleteBox(currentBox.current as KonvaBox);

      currentBox.current = undefined;

      updateCubeMenu();

      updateShareLink();

      updateCheckout();

      stage.drawDimensions();
    }
  };

  const shareLinkCopied = () => {
    toast.success(
      "Sharing url copied to your clipboard. Paste it somewhere to share.",
    );
  };

  const viewCart = (view: boolean) => {
    if (view) {
      uiService.showCart();
    } else {
      uiService.hideCart();
    }
  };

  const toggleView = () => {
    stage.toggleView();

    setView();

    updateShareLink();
  };

  const toggleColor = () => {
    const selected = currentBox.current;

    Controller.toggleColor(selected);

    if (
      selected &&
      !boxService.getKonvoBoxes().includes(selected)
    ) {
      currentBox.current = undefined;
    }

    stage.drawBoxes();

    updateShareLink();

    updateCheckout();
  };

  const zoomIn = () => {
    stage.zoom(true);

    updateShareLink();
  };

  const zoomOut = () => {
    stage.zoom(false);

    updateShareLink();
  };

  return (
    <div className="h-full flex overflow-hidden bg-gray-100">
      <div className="hidden md:flex md:flex-shrink-0">
        <Menu
          addShelf={addShelf}
          toggleBackVisibility={toggleBackVisibility}
          toggleDoorVisibility={toggleDoorVisibility}
          toggleShelfVisibility={toggleShelfVisibility}
          toggleCrossDivider={toggleCrossDivider}
          toggleCornerDirection={toggleCornerDirection}
          toggleTopDrawerVisibility={toggleTopDrawerVisibility}
          toggleMiddleDrawerVisibility={toggleMiddleDrawerVisibility}
          toggleBottomDrawerVisibility={toggleBottomDrawerVisibility}
          deleteShelf={deleteShelf}
          shareLinkCopied={shareLinkCopied}
          viewCart={viewCart}
        />
      </div>
      <div className="flex flex-col w-0 flex-1">
        <main
          id="main"
          className="flex-1 relative z-0 focus:outline-none overflow-hidden"
        >
          <div className="max-w-7xl">
            <ControlButtons
              toggleView={toggleView}
              toggleColor={toggleColor}
              zoomOut={zoomOut}
              zoomIn={zoomIn}
            />
            <div id="scroll-container" className="pt-16">
              <div id="large-container">
                <div id="container"></div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <Cart />

      <ToastContainer
        newestOnTop={false}
        autoClose={5000}
        position={"bottom-right"}
        closeButton={false}
        hideProgressBar={true}
        closeOnClick={true}
        theme={"colored"}
        transition={Zoom}
      />
    </div>
  );
};
